import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  tenantAdminService,
  PlatformAuditLog,
} from '@/features/branding/services/tenant-admin.service';

export const Route = createFileRoute('/system/logs')({
  component: SystemLogsPage,
});

const EVENT_TYPE_OPTIONS = [
  { value: 'all', label: 'Todos os eventos' },
  { value: 'tenant.created', label: 'tenant.created' },
  { value: 'subscription.assigned', label: 'subscription.assigned' },
  { value: 'subscription.updated', label: 'subscription.updated' },
  { value: 'subscription.credits_updated', label: 'subscription.credits_updated' },
  { value: 'addon.activated', label: 'addon.activated' },
  { value: 'addon.cancelled', label: 'addon.cancelled' },
  { value: 'addon.expired', label: 'addon.expired' },
  { value: 'feature_flag.override_set', label: 'feature_flag.override_set' },
  { value: 'feature_flag.override_removed', label: 'feature_flag.override_removed' },
  { value: 'feature_flag.admin_bypass', label: 'feature_flag.admin_bypass' },
  { value: 'system_user.created', label: 'system_user.created' },
  { value: 'system_user.updated', label: 'system_user.updated' },
  { value: 'system_user.deleted', label: 'system_user.deleted' },
];

const EVENT_BADGE_COLORS: Record<string, string> = {
  'tenant.created': 'bg-emerald-900 text-emerald-300',
  'subscription.assigned': 'bg-blue-900 text-blue-300',
  'subscription.updated': 'bg-blue-900 text-blue-300',
  'subscription.credits_updated': 'bg-cyan-900 text-cyan-300',
  'addon.activated': 'bg-purple-900 text-purple-300',
  'addon.cancelled': 'bg-orange-900 text-orange-300',
  'addon.expired': 'bg-red-900 text-red-300',
  'feature_flag.override_set': 'bg-yellow-900 text-yellow-300',
  'feature_flag.override_removed': 'bg-yellow-900 text-yellow-300',
  'feature_flag.admin_bypass': 'bg-red-900 text-red-300',
  'system_user.created': 'bg-indigo-900 text-indigo-300',
  'system_user.updated': 'bg-indigo-900 text-indigo-300',
  'system_user.deleted': 'bg-red-900 text-red-300',
};

const PAGE_SIZE = 50;

// ─── Linha expandível com payload ────────────────────────────────────────────

function LogRow({ log }: { log: PlatformAuditLog }) {
  const [expanded, setExpanded] = useState(false);
  const hasPayload = log.payload && Object.keys(log.payload).length > 0;
  const badgeClass = EVENT_BADGE_COLORS[log.eventType] ?? 'bg-gray-800 text-gray-300';

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <>
      <tr
        className={`border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${hasPayload ? 'cursor-pointer' : ''}`}
        onClick={() => hasPayload && setExpanded((v) => !v)}
      >
        <td className="py-3 pr-3 pl-2 w-4">
          {hasPayload ? (
            expanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
            )
          ) : null}
        </td>
        <td className="py-3 pr-4 text-xs text-gray-400 whitespace-nowrap font-mono">
          {formatDate(log.createdAt)}
        </td>
        <td className="py-3 pr-4">
          <span className={`text-xs px-2 py-0.5 rounded font-mono font-medium ${badgeClass}`}>
            {log.eventType}
          </span>
        </td>
        <td className="py-3 pr-4 text-xs">
          <span
            className={`px-1.5 py-0.5 rounded text-xs font-medium ${
              log.actorType === 'system_user'
                ? 'bg-indigo-900/50 text-indigo-300'
                : 'bg-gray-800 text-gray-400'
            }`}
          >
            {log.actorType === 'system_user' ? 'system' : 'tenant'}
          </span>
        </td>
        <td className="py-3 pr-4 text-xs text-gray-500 font-mono max-w-[160px] truncate">
          {log.actorId ?? <span className="italic text-gray-700">—</span>}
        </td>
        <td className="py-3 pr-4 text-xs text-gray-500 font-mono max-w-[160px] truncate">
          {log.tenantId ?? <span className="italic text-gray-700">—</span>}
        </td>
      </tr>
      {expanded && hasPayload && (
        <tr className="bg-gray-900 border-b border-gray-800">
          <td colSpan={6} className="py-2 pl-10 pr-4">
            <pre className="text-xs text-gray-300 bg-gray-950 rounded p-3 overflow-x-auto max-h-48 font-mono leading-relaxed">
              {JSON.stringify(log.payload, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

function SystemLogsPage() {
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [tenantIdFilter, setTenantIdFilter] = useState('');
  const [page, setPage] = useState(0);

  const { data: logs = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['platform-audit-logs', eventTypeFilter, tenantIdFilter, page],
    queryFn: () =>
      tenantAdminService.listPlatformLogs({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        eventType: eventTypeFilter !== 'all' ? eventTypeFilter : undefined,
        tenantId: tenantIdFilter.trim() || undefined,
      }),
  });

  const clearFilters = () => {
    setEventTypeFilter('all');
    setTenantIdFilter('');
    setPage(0);
  };

  const hasFilters = eventTypeFilter !== 'all' || tenantIdFilter.trim();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Logs da Plataforma</h1>
          <p className="text-sm text-gray-400 mt-1">
            Registro de todas as ações administrativas realizadas
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-gray-400 hover:text-white gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-gray-400 font-medium">Tipo de evento</span>
          <Select value={eventTypeFilter} onValueChange={(v) => { setEventTypeFilter(v); setPage(0); }}>
            <SelectTrigger className="w-56 bg-gray-800 border-gray-700 text-gray-100 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 text-gray-100">
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className="text-sm font-mono focus:bg-gray-700 focus:text-white"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-gray-400 font-medium">Tenant ID</span>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
            <Input
              placeholder="UUID do tenant..."
              value={tenantIdFilter}
              onChange={(e) => { setTenantIdFilter(e.target.value); setPage(0); }}
              className="pl-8 w-64 bg-gray-800 border-gray-700 text-gray-100 placeholder:text-gray-600 text-sm font-mono"
            />
          </div>
        </div>

        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-gray-500 hover:text-white gap-1.5 self-end"
          >
            <X className="h-3.5 w-3.5" />
            Limpar
          </Button>
        )}
      </div>

      {/* Tabela */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-gray-400 font-medium">
            {isLoading ? 'Carregando...' : `${logs.length} registro${logs.length !== 1 ? 's' : ''} (página ${page + 1})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="text-center text-gray-500 py-12">Carregando logs...</p>
          ) : logs.length === 0 ? (
            <p className="text-center text-gray-500 py-12">Nenhum log encontrado</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-500 text-xs">
                    <th className="pb-3 pl-2 w-4" />
                    <th className="text-left pb-3 pr-4 pl-0">Timestamp</th>
                    <th className="text-left pb-3 pr-4">Evento</th>
                    <th className="text-left pb-3 pr-4">Ator</th>
                    <th className="text-left pb-3 pr-4">Actor ID</th>
                    <th className="text-left pb-3 pr-4">Tenant ID</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <LogRow key={log.id} log={log} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {(logs.length === PAGE_SIZE || page > 0) && (
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="text-gray-400 hover:text-white disabled:opacity-30"
          >
            ← Anterior
          </Button>
          <span className="text-xs text-gray-500">Página {page + 1}</span>
          <Button
            variant="ghost"
            size="sm"
            disabled={logs.length < PAGE_SIZE}
            onClick={() => setPage((p) => p + 1)}
            className="text-gray-400 hover:text-white disabled:opacity-30"
          >
            Próxima →
          </Button>
        </div>
      )}
    </div>
  );
}
